/**
 * The one task view (RFC 0325).
 *
 * This module deliberately has no imports. `installTaskElements.toString()` is
 * embedded in the sandbox bridge, so the exact implementation that the host
 * mounts is also the implementation a frame mounts. Keep every runtime value
 * the installer needs inside the function.
 */

export type TaskElementStatus =
  | 'pending'
  | 'active'
  | 'complete'
  | 'blocked'
  | 'cancelled'

export interface TaskElementTask {
  id: string
  title: string
  status: TaskElementStatus
  parentId?: string
  subtaskIds?: string[]
  blockedBy?: string[]
  description?: string
  completionNote?: string
  /** The task's record (RFC 0319 3f): asked, answered, reviewed, merged. */
  events?: Array<{ type: string; at: string; note?: string }>
  /** What the task touches (RFC 0319 3b): documents, sessions, PRs. */
  refs?: TaskElementRef[]
}

export interface TaskElementRef {
  type: string
  target: string
  role?: string
}

export interface TaskPort {
  get: (id: string) => Promise<TaskElementTask | null>
  update: (
    id: string,
    patch: {
      status?: TaskElementStatus
      title?: string
      completionNote?: string
    },
  ) => Promise<TaskElementTask>
  subscribe: (fn: () => void) => () => void
}

export interface TaskRowElement extends HTMLElement {
  task?: TaskElementTask
  taskId?: string
  port?: TaskPort
  tickable?: boolean
  compact?: boolean
  nested?: boolean
  expandable?: boolean
  expanded?: boolean
  /** Draws a trailing chevron that asks the surrounding chip to show this task alone. */
  openable?: boolean
  titleNode?: Node | null
}

export interface TaskListElement extends HTMLElement {
  tasks?: TaskElementTask[]
  port?: TaskPort
  tickable?: boolean
  openable?: boolean
}

export interface TaskChipElement extends HTMLElement {
  tasks?: TaskElementTask[]
  taskId?: string
  port?: TaskPort
  label?: string
  accessibleLabel?: string
  popoverLabel?: string
  showOpenApp?: boolean
  includeCompleted?: boolean
  placement?: 'bottom-end' | 'right-start'
  popoverWidth?: number
  attention?: boolean
  /** Names for ref targets the host knows, such as session titles. */
  refLabels?: Record<string, string>
  contentNode?: Node | null
}

/** A compact task trigger whose details are fetched only when it is opened. */
export interface TaskHoverCardElement extends HTMLElement {
  task?: TaskElementTask
  taskId?: string
  contextTasks?: TaskElementTask[]
  port?: TaskPort
}

export interface InstalledTaskElements {
  TaskRow: CustomElementConstructor
  TaskList: CustomElementConstructor
  TaskChip: CustomElementConstructor
  TaskHoverCard: CustomElementConstructor
  setDefaultPort: (port?: TaskPort) => void
}

export function installTaskElements(
  target: Window & typeof globalThis = window,
): InstalledTaskElements {
  const existing = (
    target as typeof target & { __modTaskElements?: InstalledTaskElements }
  ).__modTaskElements
  if (existing) return existing

  const doc = target.document
  const liveStatuses = ['active', 'blocked', 'pending']
  let defaultPort: TaskPort | undefined

  if (!doc.querySelector('style[data-mod-task-ui]')) {
    const style = doc.createElement('style')
    style.dataset.modTaskUi = ''
    style.textContent = `
      mod-task-row, mod-task-list, mod-task-chip { color: var(--color-foreground, var(--foreground, currentColor)); font: inherit; }
      .mod-task-row { display:flex; align-items:center; gap:8px; min-width:0; padding:3px 0; font-size:13px; }
      .mod-task-row[data-nested="true"] { padding-left:16px; }
      .mod-task-toggle { flex:none; width:14px; height:14px; border:1.5px solid var(--faint, color-mix(in srgb, currentColor 42%, transparent)); border-radius:4px; padding:0; background:transparent; color:white; display:grid; place-items:center; cursor:pointer; }
      .mod-task-toggle:hover { border-color:var(--color-brand-600, var(--brand, #2563eb)); background:transparent; }
      .mod-task-toggle[data-checked="true"] { border-color:var(--color-brand-600, var(--brand, #2563eb)); background:var(--color-brand-600, var(--brand, #2563eb)); }
      .mod-task-toggle[data-checked="true"]::after { content:""; width:7px; height:4px; border:1.5px solid currentColor; border-top:0; border-right:0; transform:rotate(-45deg) translate(.5px,-1px); }
      .mod-task-toggle:disabled { opacity:.45; cursor:default; }
      .mod-task-title { min-width:0; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .mod-task-title-button { display:flex; min-width:0; flex:1; align-items:center; gap:4px; border:0; padding:0; color:inherit; background:transparent; font:inherit; text-align:left; cursor:pointer; }
      .mod-task-chevron { flex:none; width:10px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); opacity:.6; transition:transform 120ms ease; }
      .mod-task-chevron[data-expanded="true"] { transform:rotate(90deg); }
      .mod-task-row[data-done="true"] .mod-task-title { color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); text-decoration:line-through; }
      .mod-task-status { flex:none; border-radius:999px; padding:0 6px; font-size:10.5px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); background:var(--color-muted, var(--muted, color-mix(in srgb, currentColor 8%, transparent))); }
      .mod-task-status[data-status="active"] { color:var(--color-brand-500, var(--brand, #2563eb)); background:var(--brand-soft, color-mix(in srgb, #2563eb 12%, transparent)); }
      .mod-task-status[data-status="blocked"] { color:#c46616; background:color-mix(in srgb, #f97316 14%, transparent); }
      .mod-task-status[data-status="complete"], .mod-task-status[data-status="cancelled"] { opacity:.72; }
      .mod-task-row[data-compact="true"] { display:inline-flex; padding:0; vertical-align:middle; }
      .mod-task-row[data-compact="true"] .mod-task-title-button, .mod-task-row[data-compact="true"] .mod-task-status { display:none; }
      .mod-task-list { display:block; }
      .mod-task-section-label { margin:2px 4px 5px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); font-size:10.5px; font-weight:500; }
      .mod-task-done-toggle { display:flex; width:100%; align-items:center; gap:5px; margin-top:7px; border:0; border-top:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 14%, transparent))); padding:8px 4px 2px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); background:transparent; font:inherit; font-size:11px; text-align:left; cursor:pointer; }
      .mod-task-done-toggle:hover { color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-task-done-chevron { width:10px; flex:none; opacity:.65; transition:transform 120ms ease; }
      .mod-task-done-toggle[aria-expanded="true"] .mod-task-done-chevron { transform:rotate(90deg); }
      .mod-task-chip-wrap { position:relative; display:inline-block; }
      .mod-task-chip-button { display:flex; height:24px; align-items:center; gap:4px; border:0; border-radius:6px; padding:0 6px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); background:transparent; font:inherit; font-size:11px; cursor:pointer; }
      .mod-task-chip-button:hover, .mod-task-chip-button[aria-expanded="true"] { color:var(--color-foreground, var(--foreground, currentColor)); background:var(--color-muted, var(--muted, color-mix(in srgb, currentColor 8%, transparent))); }
      .mod-task-chip-icon { font-size:13px; line-height:1; }
      .mod-task-chip-label { max-width:32ch; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .mod-task-attention { width:6px; height:6px; flex:none; border-radius:999px; background:#f97316; }
      .mod-task-popover { position:absolute; top:calc(100% + 4px); right:0; z-index:50; box-sizing:border-box; width:288px; max-height:448px; overflow:auto; border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 14%, transparent))); border-radius:10px; padding:10px; color:var(--color-foreground, var(--foreground, currentColor)); background:var(--color-background, var(--background, Canvas)); box-shadow:0 10px 28px rgba(0,0,0,.32); }
      .mod-task-popover-head { display:flex; align-items:center; margin:0 4px 6px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); font-size:11px; }
      .mod-task-open-app { margin-left:auto; border:0; padding:0; color:inherit; background:transparent; font:inherit; cursor:pointer; }
      .mod-task-open-app:hover { color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-task-hover-card { display:inline-block; position:relative; vertical-align:middle; }
      .mod-task-hover-card .mod-task-row { min-height:20px; }
      .mod-task-hover-popover { position:fixed; z-index:50; box-sizing:border-box; width:min(416px, calc(100vw - 16px)); max-height:min(360px, calc(100vh - 16px)); overflow:auto; border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 14%, transparent))); border-radius:10px; padding:12px; color:var(--color-foreground, var(--foreground, currentColor)); background:var(--color-background, var(--background, Canvas)); box-shadow:0 10px 28px rgba(0,0,0,.32); }
      .mod-task-hover-title { margin:0 0 4px; font-size:13px; font-weight:600; line-height:1.35; }
      .mod-task-hover-meta { margin:0 0 8px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); font-size:11px; }
      .mod-task-hover-description, .mod-task-hover-note { margin:8px 0 0; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); font-size:12px; line-height:1.45; white-space:pre-wrap; }
      .mod-task-history { margin:10px 0 0; padding:0; list-style:none; font-size:12px; line-height:1.45; }
      .mod-task-history li { margin:4px 0 0; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); overflow-wrap:anywhere; }
      .mod-task-history b { font-weight:600; color:var(--color-foreground, var(--foreground, currentColor)); }
      .mod-task-links { margin-top:10px; }
      .mod-task-links-label { margin-bottom:3px; font-size:10.5px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); opacity:.8; }
      .mod-task-links-list { display:flex; flex-wrap:wrap; gap:4px; }
      .mod-task-link { display:inline-flex; max-width:100%; align-items:center; gap:4px; border:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 14%, transparent))); border-radius:6px; padding:1px 6px; color:inherit; background:transparent; font:inherit; font-size:11.5px; text-decoration:none; cursor:default; }
      a.mod-task-link, button.mod-task-link { cursor:pointer; }
      a.mod-task-link:hover, button.mod-task-link:hover { border-color:var(--color-primary, #6394ff); }
      .mod-task-link i { font-style:normal; font-size:10.5px; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); }
      .mod-task-link span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .mod-task-toast { display:flex; align-items:center; gap:10px; margin:6px 2px 0; border-radius:8px; padding:6px 10px; color:var(--color-background, var(--background, Canvas)); background:var(--color-foreground, var(--foreground, currentColor)); font-size:12px; }
      .mod-task-toast span { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .mod-task-toast button { flex:none; border:0; padding:0; color:#6394ff; background:transparent; font:inherit; cursor:pointer; }
      .mod-task-note { display:flex; gap:6px; margin:6px 2px 0; }
      .mod-task-note input { flex:1; min-width:0; border:1px solid var(--color-border, var(--border, currentColor)); border-radius:6px; padding:3px 8px; color:inherit; background:transparent; font:inherit; font-size:12px; }
      .mod-task-note button { border:0; border-radius:6px; padding:0 8px; color:var(--color-background, var(--background, Canvas)); background:var(--color-foreground, var(--foreground, currentColor)); font:inherit; font-size:12px; cursor:pointer; }
      .mod-task-hover-context { margin-top:10px; border-top:1px solid var(--color-border, var(--border, color-mix(in srgb, currentColor 14%, transparent))); padding-top:7px; }
      .mod-task-popover .mod-task-row { align-items:flex-start; }
      .mod-task-popover .mod-task-toggle { margin-top:2px; }
      .mod-task-popover .mod-task-title { line-height:1.35; }
      .mod-task-popover .mod-task-row:hover .mod-task-title, .mod-task-popover .mod-task-row:focus-within .mod-task-title { white-space:normal; overflow-wrap:anywhere; }
      .mod-task-popover .mod-task-status { margin-top:1px; }
      .mod-task-open { flex:none; width:16px; height:18px; border:0; border-radius:4px; padding:0; color:var(--color-muted-foreground, var(--muted-foreground, currentColor)); background:transparent; font:inherit; font-size:14px; line-height:1; opacity:.6; cursor:pointer; }
      .mod-task-open:hover { opacity:1; color:var(--color-foreground, var(--foreground, currentColor)); background:var(--color-muted, var(--muted, color-mix(in srgb, currentColor 8%, transparent))); }
      .mod-task-detail-back { border:0; padding:0; color:inherit; background:transparent; font:inherit; cursor:pointer; }
      .mod-task-detail-back:hover { color:var(--color-foreground, var(--foreground, currentColor)); }
      @media (prefers-reduced-motion: reduce) { .mod-task-chevron, .mod-task-done-chevron { transition:none; } }
    `
    doc.head.append(style)
  }

  const statusText = (task: TaskElementTask): string => {
    if (task.status === 'blocked' && (task.blockedBy?.length ?? 0) === 0)
      return 'waiting on you'
    return task.status === 'complete' ? 'done' : task.status
  }

  /** How many of a task's latest record lines a card shows. */
  const HISTORY_LINES = 4

  /** The task's latest record lines, newest first, or null when it has none.
   *  What happened to the work is what a person hovering it wants to know:
   *  what was asked, what was picked, what closed it. */
  const historyNode = (task: TaskElementTask): HTMLElement | null => {
    const events = (task.events ?? []).filter((e) => e?.type)
    if (events.length === 0) return null
    const list = doc.createElement('ul')
    list.className = 'mod-task-history'
    list.setAttribute('aria-label', 'History')
    for (const event of events.slice(-HISTORY_LINES).reverse()) {
      const item = doc.createElement('li')
      const kind = doc.createElement('b')
      kind.textContent = String(event.type)
      item.append(kind)
      const day = String(event.at ?? '').slice(0, 10)
      const note = event.note ? ` ${event.note}` : ''
      item.append(`${day ? ` · ${day}` : ''}${note ? ` -${note}` : ''}`)
      list.append(item)
    }
    return list
  }

  /** `github:owner/repo#12` as the page it names, or null. */
  const prPage = (value: string): string | null => {
    const m = /^github:([\w.-]+)\/([\w.-]+)#(\d+)$/.exec(value)
    return m ? `https://github.com/${m[1]}/${m[2]}/pull/${m[3]}` : null
  }

  /** Where a task lives, what closed it, who worked it: its refs, as links.
   *  A document or session link asks the host to open it (`mod-task-ref`). */
  const linksNode = (
    task: TaskElementTask,
    labels: Record<string, string>,
    from: HTMLElement,
  ): HTMLElement | null => {
    const refs = (task.refs ?? []).filter((r) => r?.type && r.target)
    const seen = new Set<string>()
    const unique = (r: TaskElementRef) => {
      const key = `${r.type}:${r.target}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }
    const groups: Array<[string, TaskElementRef[]]> = [
      [
        'Where it is',
        refs.filter(
          (r) =>
            r.type !== 'session' &&
            r.role !== 'closed-by' &&
            r.role !== 'owner',
        ),
      ],
      ['Closed by', refs.filter((r) => r.role === 'closed-by')],
      ['Worked by', refs.filter((r) => r.type === 'session').filter(unique)],
    ]
    const wrap = doc.createElement('div')
    for (const [label, items] of groups) {
      if (items.length === 0) continue
      const group = doc.createElement('div')
      group.className = 'mod-task-links'
      const heading = doc.createElement('div')
      heading.className = 'mod-task-links-label'
      heading.textContent = label
      const list = doc.createElement('div')
      list.className = 'mod-task-links-list'
      for (const ref of items) {
        const page = ref.type === 'pr' ? prPage(ref.target) : null
        const opens = ref.type === 'document' || ref.type === 'session'
        const link = doc.createElement(page ? 'a' : opens ? 'button' : 'span')
        link.className = 'mod-task-link'
        const kind = doc.createElement('i')
        kind.textContent = ref.type
        const name = doc.createElement('span')
        name.textContent =
          labels[ref.target] ??
          (ref.type === 'pr'
            ? ref.target.replace(/^github:/, '')
            : ref.target.length > 24
              ? `${ref.target.slice(0, 8)}…`
              : ref.target)
        link.append(kind, name)
        if (page) {
          ;(link as HTMLAnchorElement).href = page
          ;(link as HTMLAnchorElement).target = '_blank'
          ;(link as HTMLAnchorElement).rel = 'noopener noreferrer'
        } else if (opens) {
          ;(link as HTMLButtonElement).type = 'button'
          link.onclick = () =>
            from.dispatchEvent(
              new target.CustomEvent('mod-task-ref', {
                bubbles: true,
                detail: { ref },
              }),
            )
        }
        list.append(link)
      }
      group.append(heading, list)
      wrap.append(group)
    }
    return wrap.childElementCount > 0 ? wrap : null
  }

  class TaskBoundElement extends target.HTMLElement {
    _port?: TaskPort
    _unsubscribe?: () => void

    set port(value: TaskPort | undefined) {
      this._port = value
      this._subscribe()
      this.refresh()
    }
    get port(): TaskPort | undefined {
      return this._port ?? defaultPort
    }
    connectedCallback() {
      this._subscribe()
      this.refresh()
    }
    disconnectedCallback() {
      this._unsubscribe?.()
      this._unsubscribe = undefined
    }
    _subscribe() {
      this._unsubscribe?.()
      this._unsubscribe = this.isConnected
        ? this.port?.subscribe(() => this.refresh())
        : undefined
    }
    refresh() {}
  }

  class ModTaskRow extends TaskBoundElement implements TaskRowElement {
    _task?: TaskElementTask
    _taskId?: string
    _tickable = true
    _compact = false
    _nested = false
    _expandable = false
    _expanded = false
    _openable = false
    _titleNode: Node | null = null
    _generation = 0

    set task(value: TaskElementTask | undefined) {
      this._task = value
      this.refresh()
    }
    get task() {
      return this._task
    }
    set taskId(value: string | undefined) {
      this._taskId = value
      this.refresh()
    }
    get taskId() {
      return this._taskId
    }
    set tickable(value: boolean | undefined) {
      this._tickable = value !== false
      this.refresh()
    }
    get tickable() {
      return this._tickable
    }
    set compact(value: boolean | undefined) {
      this._compact = value === true
      this.refresh()
    }
    get compact() {
      return this._compact
    }
    set nested(value: boolean | undefined) {
      this._nested = value === true
      this.refresh()
    }
    get nested() {
      return this._nested
    }
    set expandable(value: boolean | undefined) {
      this._expandable = value === true
      this.refresh()
    }
    get expandable() {
      return this._expandable
    }
    set expanded(value: boolean | undefined) {
      this._expanded = value === true
      this.refresh()
    }
    get expanded() {
      return this._expanded
    }
    set openable(value: boolean | undefined) {
      this._openable = value === true
      this.refresh()
    }
    get openable() {
      return this._openable
    }
    set titleNode(value: Node | null | undefined) {
      this._titleNode = value ?? null
      this.refresh()
    }
    get titleNode() {
      return this._titleNode
    }

    async refresh() {
      const generation = ++this._generation
      if (this._taskId && this.port) {
        const loaded = await this.port.get(this._taskId).catch(() => null)
        if (generation !== this._generation) return
        if (loaded) this._task = loaded
      }
      this._draw()
    }

    _draw() {
      const task = this._task
      if (!task) {
        this.replaceChildren()
        return
      }
      const row = doc.createElement('div')
      row.className = 'mod-task-row'
      row.dataset.done = String(task.status === 'complete')
      row.dataset.compact = String(this._compact)
      row.dataset.nested = String(this._nested)

      const toggle = doc.createElement('button')
      toggle.type = 'button'
      toggle.className = 'mod-task-toggle'
      toggle.dataset.checked = String(task.status === 'complete')
      toggle.disabled = !this._tickable || !this.port
      toggle.setAttribute(
        'aria-label',
        task.status === 'complete'
          ? `Reopen ${task.title}`
          : `Mark ${task.title} done`,
      )
      toggle.onclick = async () => {
        const previous = this._task
        if (!previous || !this.port) return
        toggle.disabled = true
        try {
          const next = await this.port.update(previous.id, {
            status: previous.status === 'complete' ? 'pending' : 'complete',
          })
          this._task = next
          this._draw()
          this.dispatchEvent(
            new target.CustomEvent('mod-task-change', {
              bubbles: true,
              detail: { previous, task: next },
            }),
          )
        } finally {
          toggle.disabled = !this._tickable || !this.port
        }
      }
      row.append(toggle)

      const titleButton = doc.createElement('button')
      titleButton.type = 'button'
      titleButton.className = 'mod-task-title-button'
      titleButton.disabled = !this._expandable
      if (this._expandable) titleButton.setAttribute('aria-label', task.title)
      const chevron = doc.createElement('span')
      chevron.className = 'mod-task-chevron'
      chevron.dataset.expanded = String(this._expanded)
      chevron.textContent = '▶'
      if (this._expandable) titleButton.append(chevron)
      const title = doc.createElement('span')
      title.className = 'mod-task-title'
      if (this._titleNode) title.append(this._titleNode)
      else title.textContent = task.title
      titleButton.append(title)
      titleButton.onclick = () => {
        if (!this._expandable) return
        this.dispatchEvent(
          new target.CustomEvent('mod-task-open', {
            bubbles: true,
            detail: { task, expanded: !this._expanded },
          }),
        )
      }
      row.append(titleButton)

      const status = doc.createElement('span')
      status.className = 'mod-task-status'
      status.dataset.status = task.status
      status.textContent = statusText(task)
      row.append(status)

      if (this._openable) {
        const open = doc.createElement('button')
        open.type = 'button'
        open.className = 'mod-task-open'
        open.textContent = '›'
        open.setAttribute('aria-label', `Open ${task.title}`)
        open.onclick = () =>
          this.dispatchEvent(
            new target.CustomEvent('mod-task-detail', {
              bubbles: true,
              detail: { task },
            }),
          )
        row.append(open)
      }
      this.replaceChildren(row)
    }
  }

  class ModTaskList extends TaskBoundElement implements TaskListElement {
    _tasks: TaskElementTask[] = []
    _tickable = true
    _openable = false
    set tasks(value: TaskElementTask[] | undefined) {
      this._tasks = value ? [...value] : []
      this.refresh()
    }
    get tasks() {
      return this._tasks
    }
    set tickable(value: boolean | undefined) {
      this._tickable = value !== false
      this.refresh()
    }
    get tickable() {
      return this._tickable
    }
    set openable(value: boolean | undefined) {
      this._openable = value === true
      this.refresh()
    }
    get openable() {
      return this._openable
    }
    refresh() {
      if (!this.isConnected) return
      const list = doc.createElement('div')
      list.className = 'mod-task-list'
      const taskIds = new Set(this._tasks.map((task) => task.id))
      const children = new Map<string, TaskElementTask[]>()
      for (const task of this._tasks) {
        if (!task.parentId || !taskIds.has(task.parentId)) continue
        const siblings = children.get(task.parentId) ?? []
        siblings.push(task)
        children.set(task.parentId, siblings)
      }
      const visited = new Set<string>()
      const appendTask = (task: TaskElementTask, depth: number) => {
        if (visited.has(task.id)) return
        visited.add(task.id)
        const row = doc.createElement('mod-task-row') as TaskRowElement
        row.task = task
        row.port = this.port
        row.tickable = this._tickable
        row.nested = depth > 0
        row.openable = this._openable
        list.append(row)
        for (const child of children.get(task.id) ?? [])
          appendTask(child, depth + 1)
      }
      for (const task of this._tasks) {
        if (!task.parentId || !taskIds.has(task.parentId)) appendTask(task, 0)
      }
      // Malformed parent cycles should still render rather than disappear.
      for (const task of this._tasks) appendTask(task, 0)
      this.replaceChildren(list)
    }
  }

  class ModTaskChip extends TaskBoundElement implements TaskChipElement {
    _tasks: TaskElementTask[] = []
    _taskId?: string
    _label?: string
    _accessibleLabel?: string
    _popoverLabel?: string
    _showOpenApp = false
    _includeCompleted = false
    _placement: 'bottom-end' | 'right-start' = 'bottom-end'
    _popoverWidth = 288
    _attention = false
    _refLabels: Record<string, string> = {}
    // A tick to done offers Undo and a note for a few seconds (RFC 0319 3d).
    _ticked?: { task: TaskElementTask; was: TaskElementStatus }
    _tickedTimer?: number
    _noting?: TaskElementTask
    _contentNode: Node | null = null
    _open = false
    _doneOpen = false
    _generation = 0
    // The single-task view: the chevron on a row swaps the list for that task
    // alone, and Back returns to the list where the reader left it.
    _detailId?: string
    _detailTask?: TaskElementTask
    _detailContext: TaskElementTask[] = []
    _detailGeneration = 0
    _listScrollTop = 0
    // The single-task view keeps at least the list's height. A shorter
    // popover would leave the pointer outside it, and a hover-revealed
    // sidebar reads that as the pointer leaving and hides itself.
    _listHeight = 0
    _drawnView?: string
    _onDocumentMouseDown = (event: Event) => {
      if (!this._open || this.contains(event.target as Node)) return
      this._close()
    }
    _onDocumentKeyDown = (event: Event) => {
      if (!this._open || (event as KeyboardEvent).key !== 'Escape') return
      if (this._detailId) this._closeDetail()
      else this._close()
    }
    _close() {
      this._open = false
      this._detailId = undefined
      this._draw()
    }
    _showDetail(id: string) {
      const popover = this.querySelector(
        '.mod-task-popover',
      ) as HTMLElement | null
      if (!this._detailId) {
        this._listScrollTop = popover?.scrollTop ?? 0
        this._listHeight = popover?.offsetHeight ?? 0
      }
      this._detailId = id
      this._detailTask = this._tasks.find((task) => task.id === id)
      this._detailContext = []
      this._draw()
      void this._loadDetail()
    }
    _closeDetail() {
      this._detailId = undefined
      this._draw()
    }
    async _loadDetail() {
      const id = this._detailId
      const port = this.port
      if (!id || !port) return
      const generation = ++this._detailGeneration
      const task = await port.get(id).catch(() => null)
      if (generation !== this._detailGeneration || !task) return
      this._detailTask = task
      const relatedIds = [
        ...(task.parentId ? [task.parentId] : []),
        ...(task.subtaskIds ?? []),
      ]
      const related = await Promise.all(
        relatedIds.map((relatedId) => port.get(relatedId).catch(() => null)),
      )
      if (generation !== this._detailGeneration) return
      this._detailContext = related.filter((item): item is TaskElementTask =>
        Boolean(item),
      )
      this._draw()
    }
    connectedCallback() {
      super.connectedCallback()
      doc.addEventListener('mousedown', this._onDocumentMouseDown)
      doc.addEventListener('keydown', this._onDocumentKeyDown)
    }
    disconnectedCallback() {
      super.disconnectedCallback()
      doc.removeEventListener('mousedown', this._onDocumentMouseDown)
      doc.removeEventListener('keydown', this._onDocumentKeyDown)
    }
    set tasks(value: TaskElementTask[] | undefined) {
      this._tasks = value ? [...value] : []
      this.refresh()
    }
    get tasks() {
      return this._tasks
    }
    set taskId(value: string | undefined) {
      this._taskId = value
      this.refresh()
    }
    get taskId() {
      return this._taskId
    }
    set label(value: string | undefined) {
      this._label = value
      this.refresh()
    }
    get label() {
      return this._label
    }
    set accessibleLabel(value: string | undefined) {
      this._accessibleLabel = value
      this.refresh()
    }
    get accessibleLabel() {
      return this._accessibleLabel
    }
    set popoverLabel(value: string | undefined) {
      this._popoverLabel = value
      this.refresh()
    }
    get popoverLabel() {
      return this._popoverLabel
    }
    set showOpenApp(value: boolean | undefined) {
      this._showOpenApp = value === true
      this.refresh()
    }
    get showOpenApp() {
      return this._showOpenApp
    }
    set includeCompleted(value: boolean | undefined) {
      this._includeCompleted = value === true
      this.refresh()
    }
    get includeCompleted() {
      return this._includeCompleted
    }
    set placement(value: 'bottom-end' | 'right-start' | undefined) {
      this._placement = value ?? 'bottom-end'
      this.refresh()
    }
    get placement() {
      return this._placement
    }
    set popoverWidth(value: number | undefined) {
      this._popoverWidth = value ?? 288
      this.refresh()
    }
    get popoverWidth() {
      return this._popoverWidth
    }
    set attention(value: boolean | undefined) {
      this._attention = value === true
      this.refresh()
    }
    get attention() {
      return this._attention
    }
    set refLabels(value: Record<string, string> | undefined) {
      this._refLabels = value ?? {}
      this.refresh()
    }
    get refLabels() {
      return this._refLabels
    }
    _onTicked(previous: TaskElementTask, next: TaskElementTask) {
      if (previous.status === 'complete' || next.status !== 'complete') return
      this._ticked = { task: next, was: previous.status }
      this._noting = undefined
      if (this._tickedTimer) target.clearTimeout(this._tickedTimer)
      this._tickedTimer = target.setTimeout(() => {
        this._ticked = undefined
        this._draw()
      }, 5000)
      this._draw()
    }
    _drawAfterTick(popover: HTMLElement) {
      const port = this.port
      if (this._noting && port) {
        const noting = this._noting
        const form = doc.createElement('form')
        form.className = 'mod-task-note'
        const input = doc.createElement('input')
        input.placeholder = 'Why it is done'
        input.setAttribute('aria-label', `Note for ${noting.title}`)
        const save = doc.createElement('button')
        save.type = 'submit'
        save.textContent = 'Save'
        form.append(input, save)
        form.onsubmit = (event) => {
          event.preventDefault()
          const note = input.value.trim()
          this._noting = undefined
          if (note) void port.update(noting.id, { completionNote: note })
          this._draw()
        }
        popover.append(form)
        target.setTimeout(() => input.focus(), 0)
        return
      }
      if (!this._ticked || !port) return
      const { task, was } = this._ticked
      const toast = doc.createElement('div')
      toast.className = 'mod-task-toast'
      toast.setAttribute('role', 'status')
      const text = doc.createElement('span')
      text.textContent = `Marked “${task.title}” done`
      const undo = doc.createElement('button')
      undo.type = 'button'
      undo.textContent = 'Undo'
      undo.onclick = () => {
        this._ticked = undefined
        void port.update(task.id, { status: was })
        this._draw()
      }
      const note = doc.createElement('button')
      note.type = 'button'
      note.textContent = 'Add a note'
      note.onclick = () => {
        this._ticked = undefined
        this._noting = task
        this._draw()
      }
      toast.append(text, undo, note)
      popover.append(toast)
    }
    set contentNode(value: Node | null | undefined) {
      this._contentNode = value ?? null
      this.refresh()
    }
    get contentNode() {
      return this._contentNode
    }
    async refresh() {
      const generation = ++this._generation
      if (this._taskId && this.port) {
        const task = await this.port.get(this._taskId).catch(() => null)
        if (generation !== this._generation) return
        this._tasks = task ? [task] : []
      }
      if (this._detailId) void this._loadDetail()
      this._draw()
    }
    _draw() {
      // A project/session registry update replaces the contents below. Keep
      // the reader's place in an open list across that redraw; otherwise any
      // background task change makes a long popover jump back to its first row.
      // Moving between the list and one task is not a redraw of the same view:
      // a task opens at its top, and Back returns to where the list was.
      const view = this._detailId ? `task:${this._detailId}` : 'list'
      const heldScrollTop =
        view === this._drawnView
          ? (this.querySelector('.mod-task-popover') as HTMLElement | null)
              ?.scrollTop
          : view === 'list'
            ? this._listScrollTop
            : 0
      this._drawnView = this._open ? view : undefined
      if (this._taskId) {
        const row = doc.createElement('mod-task-row') as TaskRowElement
        row.task = this._tasks[0]
        row.taskId = this._taskId
        row.port = this.port
        row.compact = true
        this.replaceChildren(row)
        return
      }
      const counted = this._tasks.filter((task) => task.status !== 'cancelled')
      const live = counted
        .filter((task) => liveStatuses.includes(task.status))
        .sort(
          (a, b) =>
            liveStatuses.indexOf(a.status) - liveStatuses.indexOf(b.status),
        )
      const visible = this._includeCompleted ? counted : live
      if (visible.length === 0) {
        this.replaceChildren()
        return
      }
      const done = counted.filter((task) => task.status === 'complete').length
      const wrap = doc.createElement('div')
      wrap.className = 'mod-task-chip-wrap'
      wrap.dataset.placement = this._placement
      const button = doc.createElement('button')
      button.type = 'button'
      button.className = 'mod-task-chip-button'
      button.setAttribute('aria-expanded', String(this._open))
      button.setAttribute(
        'aria-label',
        this._accessibleLabel ??
          `${live.length} ${live.length === 1 ? 'task' : 'tasks'} for this document`,
      )
      const icon = doc.createElement('span')
      icon.className = 'mod-task-chip-icon'
      icon.textContent = '☑'
      const text = doc.createElement('span')
      text.className = 'mod-task-chip-label'
      text.textContent =
        this._label ?? `${live.length} ${live.length === 1 ? 'task' : 'tasks'}`
      button.append(icon, text)
      if (done > 0 && !this._label) {
        const doneText = doc.createElement('span')
        doneText.textContent = `· ${done} done`
        button.append(doneText)
      }
      if (this._attention) {
        const attention = doc.createElement('span')
        attention.className = 'mod-task-attention'
        attention.title = 'Waiting on you'
        button.append(attention)
      }
      button.onclick = () => {
        if (this._open) {
          this._close()
          return
        }
        this._open = true
        this._draw()
      }
      wrap.append(button)
      if (this._open) {
        const popover = doc.createElement('div')
        popover.className = 'mod-task-popover'
        popover.style.width = `${this._popoverWidth}px`
        popover.setAttribute('role', 'dialog')
        popover.setAttribute(
          'aria-label',
          this._popoverLabel ?? this._accessibleLabel ?? 'Tasks',
        )
        popover.addEventListener('mod-task-detail', (event) => {
          event.stopPropagation()
          const task = (event as CustomEvent<{ task: TaskElementTask }>).detail
            .task
          this._showDetail(task.id)
        })
        popover.addEventListener('mod-task-change', (event) => {
          const { previous, task } = (
            event as CustomEvent<{
              previous: TaskElementTask
              task: TaskElementTask
            }>
          ).detail
          this._onTicked(previous, task)
        })
        if (this._detailId && !this._contentNode) this._drawDetail(popover)
        else this._drawList(popover, visible)
        this._drawAfterTick(popover)
        wrap.append(popover)
      }
      this.replaceChildren(wrap)
      this._placePopover(wrap, button, heldScrollTop)
    }
    _drawDetail(popover: HTMLElement) {
      if (this._listHeight > 0)
        popover.style.minHeight = `${this._listHeight}px`
      const head = doc.createElement('div')
      head.className = 'mod-task-popover-head'
      const back = doc.createElement('button')
      back.type = 'button'
      back.className = 'mod-task-detail-back'
      back.textContent = '‹ Tasks'
      back.setAttribute('aria-label', 'Back to tasks')
      back.onclick = () => this._closeDetail()
      head.append(back)
      popover.append(head)
      const task = this._detailTask
      if (!task) {
        const missing = doc.createElement('p')
        missing.className = 'mod-task-hover-description'
        missing.textContent = 'Loading…'
        popover.append(missing)
        return
      }
      const row = doc.createElement('mod-task-row') as TaskRowElement
      row.task = task
      row.port = this.port
      row.tickable = Boolean(this.port)
      popover.append(row)
      const description = doc.createElement('p')
      description.className = 'mod-task-hover-description'
      description.textContent = task.description || 'No description.'
      popover.append(description)
      if (task.completionNote) {
        const note = doc.createElement('p')
        note.className = 'mod-task-hover-note'
        note.textContent = `Done: ${task.completionNote}`
        popover.append(note)
      }
      const history = historyNode(task)
      if (history) popover.append(history)
      const links = linksNode(task, this._refLabels, this)
      if (links) popover.append(links)
      // Parent and subtasks: what the port loaded, plus any the list already
      // holds, so a slice shows its RFC even before the load returns.
      const context = new Map<string, TaskElementTask>()
      for (const item of this._tasks)
        if (item.id === task.parentId || item.parentId === task.id)
          context.set(item.id, item)
      for (const item of this._detailContext) context.set(item.id, item)
      const parent = task.parentId ? context.get(task.parentId) : undefined
      const subtasks = [...context.values()].filter(
        (item) => item.parentId === task.id,
      )
      const section = (label: string, items: TaskElementTask[]) => {
        if (items.length === 0) return
        const heading = doc.createElement('div')
        heading.className = 'mod-task-section-label'
        heading.style.marginTop = '10px'
        heading.textContent = label
        popover.append(heading)
        const list = doc.createElement('mod-task-list') as TaskListElement
        list.tasks = items
        list.port = this.port
        list.tickable = Boolean(this.port)
        list.openable = true
        popover.append(list)
      }
      section('Part of', parent ? [parent] : [])
      section(`Subtasks ${subtasks.length}`, subtasks)
    }
    _drawList(popover: HTMLElement, visible: TaskElementTask[]) {
      const head = doc.createElement('div')
      head.className = 'mod-task-popover-head'
      const summary = doc.createElement('span')
      summary.append(
        this._popoverLabel ??
          `${visible.length} ${visible.length === 1 ? 'task' : 'tasks'}`,
      )
      head.append(summary)
      if (this._showOpenApp) {
        const open = doc.createElement('button')
        open.type = 'button'
        open.className = 'mod-task-open-app'
        open.textContent = 'Open Tasks'
        open.onclick = () =>
          this.dispatchEvent(
            new target.CustomEvent('mod-open-tasks', { bubbles: true }),
          )
        head.append(open)
      }
      popover.append(head)
      if (this._contentNode) {
        popover.append(this._contentNode)
      } else {
        const openTasks = this._includeCompleted
          ? visible.filter((task) => task.status !== 'complete')
          : visible
        const doneTasks = this._includeCompleted
          ? visible.filter((task) => task.status === 'complete')
          : []
        if (this._includeCompleted && openTasks.length > 0) {
          const label = doc.createElement('div')
          label.className = 'mod-task-section-label'
          label.textContent = `Open ${openTasks.length}`
          popover.append(label)
        }
        const list = doc.createElement('mod-task-list') as TaskListElement
        list.tasks = openTasks
        list.port = this.port
        list.tickable = Boolean(this.port)
        list.openable = true
        popover.append(list)
        if (doneTasks.length > 0) {
          const toggle = doc.createElement('button')
          toggle.type = 'button'
          toggle.className = 'mod-task-done-toggle'
          toggle.setAttribute('aria-expanded', String(this._doneOpen))
          const chevron = doc.createElement('span')
          chevron.className = 'mod-task-done-chevron'
          chevron.setAttribute('aria-hidden', 'true')
          chevron.textContent = '▶'
          const label = doc.createElement('span')
          label.textContent = `Done ${doneTasks.length}`
          toggle.append(chevron, label)
          toggle.onclick = () => {
            this._doneOpen = !this._doneOpen
            this._draw()
          }
          popover.append(toggle)
          if (this._doneOpen) {
            const doneList = doc.createElement(
              'mod-task-list',
            ) as TaskListElement
            doneList.tasks = doneTasks
            doneList.port = this.port
            doneList.tickable = Boolean(this.port)
            doneList.openable = true
            popover.append(doneList)
          }
        }
      }
    }
    _placePopover(
      wrap: HTMLElement,
      button: HTMLElement,
      heldScrollTop: number | undefined,
    ) {
      const openPopover = this._open
        ? (wrap.querySelector('.mod-task-popover') as HTMLElement | null)
        : null
      if (openPopover && heldScrollTop !== undefined)
        openPopover.scrollTop = heldScrollTop
      if (this._open && openPopover) {
        const popover = openPopover
        const rect = button.getBoundingClientRect()
        const gutter = 4
        const viewportGutter = 8
        const popoverRect = popover.getBoundingClientRect()
        const width = popoverRect.width || this._popoverWidth
        const height = popoverRect.height || popover.scrollHeight
        const left =
          this._placement === 'right-start'
            ? Math.min(
                rect.right + gutter,
                Math.max(
                  viewportGutter,
                  target.innerWidth - width - viewportGutter,
                ),
              )
            : Math.min(
                Math.max(viewportGutter, rect.right - width),
                Math.max(
                  viewportGutter,
                  target.innerWidth - width - viewportGutter,
                ),
              )
        popover.style.position = 'fixed'
        popover.style.right = 'auto'
        popover.style.left = `${left}px`
        if (this._placement === 'right-start') {
          const top = Math.max(viewportGutter, rect.top)
          popover.style.top = `${top}px`
          popover.style.maxHeight = `${Math.max(0, target.innerHeight - top - viewportGutter)}px`
        } else {
          const spaceBelow =
            target.innerHeight - rect.bottom - gutter - viewportGutter
          const spaceAbove = rect.top - gutter - viewportGutter
          const opensAbove = height > spaceBelow && spaceAbove > spaceBelow
          popover.dataset.side = opensAbove ? 'top' : 'bottom'
          if (opensAbove) {
            popover.style.top = 'auto'
            popover.style.bottom = `${target.innerHeight - rect.top + gutter}px`
            popover.style.maxHeight = `${Math.max(0, spaceAbove)}px`
          } else {
            popover.style.top = `${rect.bottom + gutter}px`
            popover.style.bottom = 'auto'
            popover.style.maxHeight = `${Math.max(0, spaceBelow)}px`
          }
        }
      }
    }
  }

  let openHoverCard: (TaskHoverCardElement & { close: () => void }) | undefined

  class ModTaskHoverCard
    extends TaskBoundElement
    implements TaskHoverCardElement
  {
    _task?: TaskElementTask
    _taskId?: string
    _contextTasks: TaskElementTask[] = []
    _full?: TaskElementTask | null
    _open = false
    _loaded = false
    _generation = 0
    _closeTimer?: number
    _onDocumentMouseDown = (event: Event) => {
      if (this._open && !this.contains(event.target as Node)) this.close()
    }
    _onDocumentKeyDown = (event: Event) => {
      if (this._open && (event as KeyboardEvent).key === 'Escape') {
        event.preventDefault()
        this.close()
      }
    }
    set task(value: TaskElementTask | undefined) {
      this._task = value
      this._taskId = value?.id
      this._loaded = false
      this._full = undefined
      this.refresh()
    }
    get task() {
      return this._task
    }
    set taskId(value: string | undefined) {
      this._taskId = value
      this.refresh()
    }
    get taskId() {
      return this._taskId
    }
    set contextTasks(value: TaskElementTask[] | undefined) {
      this._contextTasks = value ? [...value] : []
      this.refresh()
    }
    get contextTasks() {
      return this._contextTasks
    }
    connectedCallback() {
      super.connectedCallback()
      doc.addEventListener('mousedown', this._onDocumentMouseDown)
      doc.addEventListener('keydown', this._onDocumentKeyDown)
    }
    disconnectedCallback() {
      super.disconnectedCallback()
      doc.removeEventListener('mousedown', this._onDocumentMouseDown)
      doc.removeEventListener('keydown', this._onDocumentKeyDown)
      if (this._closeTimer) target.clearTimeout(this._closeTimer)
      if (openHoverCard === this) openHoverCard = undefined
    }
    refresh() {
      this._draw()
    }
    open() {
      if (this._closeTimer) target.clearTimeout(this._closeTimer)
      if (openHoverCard !== this) openHoverCard?.close()
      openHoverCard = this
      if (!this._task && this._taskId && this.port) {
        const generation = ++this._generation
        void this.port.get(this._taskId).then((task) => {
          if (generation !== this._generation || !task) return
          this._task = task
          this._draw()
        })
      }
      this._open = true
      this._draw()
      if (!this._loaded && this._taskId && this.port) {
        this._loaded = true
        const generation = ++this._generation
        void this.port.get(this._taskId).then(async (task) => {
          if (generation !== this._generation) return
          this._full = task
          if (task) {
            const relatedIds = [
              ...(task.parentId ? [task.parentId] : []),
              ...(task.subtaskIds ?? []),
            ]
            const related = await Promise.all(
              relatedIds.map((id) => this.port?.get(id)),
            )
            if (generation !== this._generation) return
            this._contextTasks = related.filter(
              (item): item is TaskElementTask => Boolean(item),
            )
          }
          this._draw()
        })
      }
    }
    close() {
      if (this._closeTimer) target.clearTimeout(this._closeTimer)
      if (openHoverCard === this) openHoverCard = undefined
      this._open = false
      this._draw()
    }
    scheduleClose() {
      if (this._closeTimer) target.clearTimeout(this._closeTimer)
      this._closeTimer = target.setTimeout(
        () => this.close(),
        120,
      ) as unknown as number
    }
    _draw() {
      const task = this._task
      if (!task) {
        this.replaceChildren()
        return
      }
      const wrap = doc.createElement('div')
      wrap.className = 'mod-task-hover-card'
      wrap.onmouseenter = () => this.open()
      wrap.onmouseleave = () => this.scheduleClose()
      wrap.addEventListener('focusin', () => this.open())
      wrap.addEventListener('focusout', (event) => {
        if (!wrap.contains((event as FocusEvent).relatedTarget as Node | null))
          this.scheduleClose()
      })
      const row = doc.createElement('mod-task-row') as TaskRowElement
      row.task = task
      row.port = this.port
      row.compact = true
      row.setAttribute(
        'aria-describedby',
        this._open ? `task-hover-${task.id}` : '',
      )
      row.onclick = () => this.open()
      wrap.append(row)
      if (this._open) {
        const card = doc.createElement('section')
        card.className = 'mod-task-hover-popover'
        card.id = `task-hover-${task.id}`
        card.setAttribute('role', 'dialog')
        card.setAttribute('aria-label', `Task details: ${task.title}`)
        const title = doc.createElement('p')
        title.className = 'mod-task-hover-title'
        title.textContent = task.title
        card.append(title)
        const meta = doc.createElement('p')
        meta.className = 'mod-task-hover-meta'
        meta.textContent = statusText(task)
        card.append(meta)
        if (!this._full) {
          const loading = doc.createElement('p')
          loading.className = 'mod-task-hover-description'
          loading.textContent = 'Loading details…'
          card.append(loading)
        } else {
          const description = doc.createElement('p')
          description.className = 'mod-task-hover-description'
          description.textContent = this._full.description || 'No description.'
          card.append(description)
          if (this._full.completionNote) {
            const note = doc.createElement('p')
            note.className = 'mod-task-hover-note'
            note.textContent = `Done: ${this._full.completionNote}`
            card.append(note)
          }
          const history = historyNode(this._full)
          if (history) card.append(history)
        }
        const context = this._contextTasks.filter((item) => item.id !== task.id)
        if (context.length) {
          const contextList = doc.createElement('div')
          contextList.className = 'mod-task-hover-context'
          for (const item of context) {
            const itemRow = doc.createElement('mod-task-row') as TaskRowElement
            itemRow.task = item
            itemRow.port = this.port
            itemRow.tickable = false
            itemRow.nested = Boolean(item.parentId)
            contextList.append(itemRow)
          }
          card.append(contextList)
        }
        wrap.append(card)
      }
      this.replaceChildren(wrap)
      const card = wrap.querySelector(
        '.mod-task-hover-popover',
      ) as HTMLElement | null
      const toggle = wrap.querySelector(
        '.mod-task-toggle',
      ) as HTMLElement | null
      if (card && toggle) this._position(card, toggle)
    }
    _position(card: HTMLElement, trigger: HTMLElement) {
      const rect = trigger.getBoundingClientRect()
      const bounds = card.getBoundingClientRect()
      const gutter = 8
      const width = bounds.width || 416
      const height = bounds.height || card.scrollHeight
      const spaceBelow = target.innerHeight - rect.bottom - gutter
      const spaceAbove = rect.top - gutter
      const opensAbove = height > spaceBelow && spaceAbove > spaceBelow
      const left = Math.min(
        Math.max(gutter, rect.left),
        Math.max(gutter, target.innerWidth - width - gutter),
      )
      card.style.left = `${left}px`
      card.style.right = 'auto'
      card.dataset.side = opensAbove ? 'top' : 'bottom'
      if (opensAbove) {
        card.style.top = 'auto'
        card.style.bottom = `${target.innerHeight - rect.top + gutter}px`
        card.style.maxHeight = `${Math.max(120, spaceAbove)}px`
      } else {
        card.style.top = `${rect.bottom + gutter}px`
        card.style.bottom = 'auto'
        card.style.maxHeight = `${Math.max(120, spaceBelow)}px`
      }
    }
  }

  if (!target.customElements.get('mod-task-row'))
    target.customElements.define('mod-task-row', ModTaskRow)
  if (!target.customElements.get('mod-task-list'))
    target.customElements.define('mod-task-list', ModTaskList)
  if (!target.customElements.get('mod-task-chip'))
    target.customElements.define('mod-task-chip', ModTaskChip)
  if (!target.customElements.get('mod-task-hover-card'))
    target.customElements.define('mod-task-hover-card', ModTaskHoverCard)

  const installed: InstalledTaskElements = {
    TaskRow: target.customElements.get(
      'mod-task-row',
    ) as CustomElementConstructor,
    TaskList: target.customElements.get(
      'mod-task-list',
    ) as CustomElementConstructor,
    TaskChip: target.customElements.get(
      'mod-task-chip',
    ) as CustomElementConstructor,
    TaskHoverCard: target.customElements.get(
      'mod-task-hover-card',
    ) as CustomElementConstructor,
    setDefaultPort: (port) => {
      defaultPort = port
      doc
        .querySelectorAll(
          'mod-task-row, mod-task-list, mod-task-chip, mod-task-hover-card',
        )
        .forEach((element) => (element as TaskBoundElement)._subscribe?.())
    },
  }
  ;(
    target as typeof target & { __modTaskElements?: InstalledTaskElements }
  ).__modTaskElements = installed
  return installed
}

export function ensureTaskElements(): InstalledTaskElements {
  return installTaskElements(window)
}
